import { useState } from 'react';
import { Plus, Wallet } from 'lucide-react';
import {
  VENDOR_PAYMENT_METHOD_SPECS,
  type VendorPaymentMethod,
  type VendorPaymentMethodInput,
} from '@peles/shared';
import { useDialog } from '../../../context/DialogContext';
import {
  useAddVendorPaymentMethod,
  useRemoveVendorPaymentMethod,
  useUpdateVendorPaymentMethod,
} from '../../../hooks/useVendors';
import { PaymentMethodCard, type PaymentTarget } from './PaymentMethodCard';
import { PaymentMethodFormModal } from './PaymentMethodFormModal';

export interface VendorPaymentMethodsProps {
  vendorId: string;
  methods: VendorPaymentMethod[];
  /** Add, edit and remove (PLANNER+). */
  canManage: boolean;
  /** An amount to pay — each Venmo / PayPal card then offers to pay it. */
  payment?: PaymentTarget;
}

/**
 * A vendor's payment methods: the vendor page's section and the review opened
 * from an expense both render it. It owns its add/edit dialog.
 */
export function VendorPaymentMethods({
  vendorId,
  methods,
  canManage,
  payment,
}: VendorPaymentMethodsProps) {
  const { confirm, alert } = useDialog();
  const add = useAddVendorPaymentMethod(vendorId);
  const update = useUpdateVendorPaymentMethod(vendorId);
  const remove = useRemoveVendorPaymentMethod(vendorId);
  const [editing, setEditing] = useState<VendorPaymentMethod | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const openForm = (method: VendorPaymentMethod | null) => {
    setEditing(method);
    setFormOpen(true);
  };

  const save = (input: VendorPaymentMethodInput) =>
    editing ? update.mutateAsync({ methodId: editing.id, input }) : add.mutateAsync(input);

  const askRemove = async (method: VendorPaymentMethod) => {
    const label = VENDOR_PAYMENT_METHOD_SPECS[method.type].label;
    if (
      !(await confirm({
        title: 'Remove payment method',
        message: `Remove ${method.label ? `"${method.label}" (${label})` : label} from this vendor?`,
        confirmLabel: 'Remove',
        variant: 'danger',
      }))
    ) {
      return;
    }
    try {
      await remove.mutateAsync(method.id);
    } catch {
      await alert({
        title: 'Not removed',
        message: 'The payment method could not be removed. Try again.',
        variant: 'error',
      });
    }
  };

  return (
    <div className="space-y-3">
      {methods.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-6 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg">
          <Wallet size={20} className="text-gray-400" aria-hidden="true" />
          No payment methods yet.
        </div>
      ) : (
        <ul className="space-y-2" aria-label="Payment methods">
          {methods.map((method) => (
            <PaymentMethodCard
              key={method.id}
              method={method}
              payment={payment}
              onEdit={canManage ? () => openForm(method) : undefined}
              onDelete={canManage ? () => void askRemove(method) : undefined}
            />
          ))}
        </ul>
      )}
      {canManage && (
        <button
          type="button"
          onClick={() => openForm(null)}
          className="min-h-11 sm:min-h-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          <Plus size={16} />
          Add payment method
        </button>
      )}
      <PaymentMethodFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        method={editing}
        onSave={save}
        saving={add.isPending || update.isPending}
      />
    </div>
  );
}
